import React, { useState, useRef, useEffect } from 'react';
import { FaSave, FaPlay, FaPause, FaPencilAlt, FaFlask } from 'react-icons/fa';
import Modal from '../shared/Modal';

const CustomAgentToolbar = ({ customAgent, onSave, onDeploy, onPause, onBack, saving, isActive, onNameChange, nodes = [], edges = [], loading = false, deployStatus = null, onTest }) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [customAgentName, setCustomAgentName] = useState(customAgent?.name || '');
  const [customAgentDescription, setCustomAgentDescription] = useState(customAgent?.description || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(customAgent?.name || 'Untitled Custom Agent');
  const nameInputRef = useRef(null);
  const editStartTime = useRef(null);

  // Update edited name when customAgent changes
  // Watch for customAgent.id and customAgent.name changes specifically to ensure updates
  useEffect(() => {
    if (customAgent && customAgent.id) {
      const customAgentName = customAgent.name || 'Untitled Custom Agent';
      console.log('CustomAgentToolbar: Updating name from customAgent prop:', { id: customAgent.id, name: customAgentName, hasName: !!customAgent.name });
      setEditedName(customAgentName);
      setCustomAgentName(customAgentName);
      setCustomAgentDescription(customAgent.description || '');
    } else {
      // Reset to default if no customAgent
      console.log('CustomAgentToolbar: Resetting to default (no customAgent)');
      setEditedName('Untitled Custom Agent');
      setCustomAgentName('');
      setCustomAgentDescription('');
    }
  }, [customAgent?.id, customAgent?.name, customAgent]);

  const handleSaveClick = async () => {
    if (customAgent) {
      // If customAgent exists, save with current edited name (in case user edited it)
      const nameToSave = editedName.trim() || customAgent.name || 'Untitled Custom Agent';
      await onSave(nameToSave, customAgent.description);
    } else {
      // If new customAgent, show modal with the edited name pre-filled
      setCustomAgentName(editedName);
      setShowSaveModal(true);
    }
  };

  const handleSaveConfirm = async () => {
    if (!customAgentName.trim()) {
      alert('Please enter a customAgent name');
      return;
    }

    const success = await onSave(customAgentName, customAgentDescription);
    if (success) {
      setShowSaveModal(false);
    }
  };

  // Handle inline name editing
  const handleNameClick = () => {
    // Allow editing for both new and existing customAgents
    editStartTime.current = Date.now();
    setIsEditingName(true);
  };

  const handleNameBlur = (e) => {
    // Prevent blur if clicked too quickly (within 100ms)
    const timeSinceEdit = Date.now() - (editStartTime.current || 0);
    if (timeSinceEdit < 100) {
      // Refocus the input
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 0);
      return;
    }

    setIsEditingName(false);
    
    // If empty name, reset to original or default
    if (!editedName.trim()) {
      setEditedName(customAgent?.name || 'Untitled Custom Agent');
      return;
    }
    
    // For existing customAgents, save the name change immediately
    if (customAgent && editedName.trim() && editedName.trim() !== customAgent.name) {
      // Save the name change
      if (onSave) {
        onSave(editedName.trim(), customAgent.description);
      }
      // Also call onNameChange if provided (for immediate UI update)
      if (onNameChange) {
        onNameChange(editedName.trim());
      }
    }
    // For new customAgents, just keep the edited name in state
    // It will be used when the customAgent is first saved
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Force save by setting time way in the past
      editStartTime.current = 0;
      nameInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setEditedName(customAgent?.name || 'Untitled Custom Agent');
      editStartTime.current = 0;
      setIsEditingName(false);
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  return (
    <>
      <div className="custom-agent-toolbar">
        <div className="toolbar-left">
          <button
            className="toolbar-btn toolbar-btn-back"
            onClick={onBack}
            title="Back to Custom Agents"
          >
            <span style={{ marginRight: '8px' }}>←</span>
            Back to Custom Agents
          </button>
          <div className="custom-agent-title-container">
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                className="custom-agent-title-input"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                placeholder="Untitled Custom Agent"
              />
            ) : (
              <div className="custom-agent-title-wrapper">
                <h2 
                  className="custom-agent-title"
                  onClick={handleNameClick}
                  title="Click to edit customAgent name"
                >
                  {editedName}
                  <FaPencilAlt className="edit-icon" />
                </h2>
                {customAgent?.status && (
                  <span className={`custom-agent-status custom-agent-status-${customAgent.status}`}>
                    {customAgent.status}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="toolbar-right">
          <button
            type="button"
            className="toolbar-btn toolbar-btn-secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSaveClick();
            }}
            disabled={saving}
            title="Save Custom Agent"
          >
            <FaSave /> {saving ? 'Saving...' : 'Save'}
          </button>

          {(() => {
            // Check if there's a trigger node to enable test button
            const triggerNode = nodes.find((node) => {
              const nodeType = node.data?.nodeType;
              return nodeType && nodeType.startsWith('trigger.');
            });
            const hasTriggerNode = !!triggerNode;

            if (!hasTriggerNode) return null;

            // Disable test button if customAgent doesn't exist (not saved yet)
            const isTestDisabled = !customAgent || !customAgent.id || saving || loading;

            return (
              <button
                type="button"
                className="toolbar-btn toolbar-btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onTest && !isTestDisabled) {
                    onTest();
                  }
                }}
                disabled={isTestDisabled}
                title={
                  !customAgent || !customAgent.id 
                    ? "Save Custom Agent first to test" 
                    : "Test Custom Agent with sample data"
                }
              >
                <FaFlask /> Test
              </button>
            );
          })()}

          {(() => {
            // Check if there's a trigger node
            const triggerNode = nodes.find((node) => {
              const nodeType = node.data?.nodeType;
              return nodeType && nodeType.startsWith('trigger.');
            });
            
            // Check if there are any action nodes
            const actionNodes = nodes.filter((node) => {
              const nodeType = node.data?.nodeType;
              return nodeType && nodeType.startsWith('action.');
            });
            
            // Check if all nodes form a single connected graph with trigger connected to action
            // Returns: { valid: boolean, hasDisconnectedNodes: boolean, hasPathToAction: boolean }
            const validateCustomAgent = () => {
              if (!triggerNode || actionNodes.length === 0) {
                return { valid: false, hasDisconnectedNodes: false, hasPathToAction: false };
              }
              
              // If no edges, nodes are disconnected
              if (!edges || edges.length === 0) {
                return { valid: false, hasDisconnectedNodes: nodes.length > 1, hasPathToAction: false };
              }
              
              // Check if there's a path from trigger to at least one action node
              // Also check if all nodes are reachable (single connected graph)
              const actionNodeIds = new Set(actionNodes.map(n => n.id));
              const visited = new Set();
              const queue = [triggerNode.id];
              visited.add(triggerNode.id);
              let foundAction = false;
              
              while (queue.length > 0) {
                const currentNodeId = queue.shift();
                
                // Check if we reached any action node
                if (actionNodeIds.has(currentNodeId)) {
                  foundAction = true;
                }
                
                // Find all nodes connected from current node
                const outgoingEdges = edges.filter(edge => edge.source === currentNodeId);
                for (const edge of outgoingEdges) {
                  if (!visited.has(edge.target)) {
                    visited.add(edge.target);
                    queue.push(edge.target);
                  }
                }
              }
              
              // Check if all nodes are reachable from the trigger (single connected graph)
              const allNodeIds = new Set(nodes.map(n => n.id));
              const unreachableNodes = [...allNodeIds].filter(id => !visited.has(id));
              const hasDisconnectedNodes = unreachableNodes.length > 0;
              
              const valid = foundAction && !hasDisconnectedNodes;
              
              return { 
                valid, 
                hasDisconnectedNodes, 
                hasPathToAction: foundAction 
              };
            };
            
            const hasTriggerNode = !!triggerNode;
            const hasActionNode = actionNodes.length > 0;
            const customAgentValidation = validateCustomAgent();
            const isValidCustomAgent = customAgentValidation.valid;
            
            // Build tooltip message for missing requirements
            const getDeployTooltip = () => {
              if (isActive) {
                return "CustomAgent is already active";
              }
              
              const missing = [];
              if (!hasTriggerNode) {
                missing.push('trigger signal node');
              }
              if (!hasActionNode) {
                missing.push('action node');
              }
              
              // Check for disconnected nodes (multiple independent customAgents)
              if (hasTriggerNode && hasActionNode && !isValidCustomAgent) {
                if (customAgentValidation.hasDisconnectedNodes) {
                  return "All nodes must be connected in a single customAgent. Remove disconnected nodes or connect them.";
                }
                if (!customAgentValidation.hasPathToAction) {
                  return "Trigger signal node and action node must be connected";
                }
              }
              
              if (missing.length > 0) {
                if (missing.length === 2) {
                  return "Missing: trigger signal node and action node";
                } else {
                  return `Missing: ${missing[0]}`;
                }
              }
              
              const triggerType = triggerNode?.data?.config?.triggerType || 'always_on';
              const isOneTimeTrigger = triggerType === 'one_time';
              return isOneTimeTrigger ? "Deploy and trigger customAgent" : "Deploy customAgent";
            };
            
            const triggerType = triggerNode?.data?.config?.triggerType || 'always_on';
            const isOneTimeTrigger = triggerType === 'one_time';
            const isAlwaysOnTrigger = triggerType === 'always_on';

            // For always-on triggers that are active, show pause button
            if (isAlwaysOnTrigger && isActive) {
              return (
                <button
                  className="toolbar-btn toolbar-btn-warning"
                  onClick={onPause}
                  disabled={!customAgent || saving || loading}
                  title="Pause Custom Agent"
                >
                  <FaPause /> Pause
                </button>
              );
            }

            // For all other cases, show deploy button
            // For one-time triggers, allow deploy even when active (they should be paused after execution)
            // For always-on triggers, disable deploy when active (use pause button instead)
            const getDeployButtonText = () => {
              if (deployStatus === 'deploying') return 'Deploying...';
              if (deployStatus === 'triggering') return 'Triggering...';
              if (deployStatus === 'success') return 'Deployed!';
              if (deployStatus === 'error') return 'Error';
              if (loading) return 'Deploying...';
              return 'Deploy';
            };
            
            const getDeployButtonClass = () => {
              if (deployStatus === 'success') return 'toolbar-btn toolbar-btn-success';
              if (deployStatus === 'error') return 'toolbar-btn toolbar-btn-error';
              return 'toolbar-btn toolbar-btn-primary';
            };
            
            // Disable deploy button if:
            // - No customAgent
            // - Saving or loading
            // - Currently deploying/triggering
            // - Always-on trigger is active (should use pause button instead)
            // - Missing trigger node
            // - Missing action node
            // - Invalid customAgent (not connected or has disconnected nodes)
            const isDeployDisabled = !customAgent || 
              saving || 
              loading || 
              deployStatus === 'deploying' || 
              deployStatus === 'triggering' || 
              (!isOneTimeTrigger && isActive) ||
              !hasTriggerNode ||
              !hasActionNode ||
              !isValidCustomAgent;
            
            return (
              <button
                className={getDeployButtonClass()}
                onClick={onDeploy}
                disabled={isDeployDisabled}
                title={getDeployTooltip()}
              >
                <FaPlay /> {getDeployButtonText()}
              </button>
            );
          })()}
        </div>
      </div>

      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Save CustomAgent"
        size="medium"
      >
        <div className="save-customAgent-modal">
          <div className="form-group">
            <label>CustomAgent Name *</label>
            <input
              type="text"
              value={customAgentName}
              onChange={(e) => setCustomAgentName(e.target.value)}
              placeholder="Enter customAgent name"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={customAgentDescription}
              onChange={(e) => setCustomAgentDescription(e.target.value)}
              placeholder="Enter customAgent description (optional)"
              rows="3"
            />
          </div>

          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowSaveModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSaveConfirm();
              }}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save CustomAgent'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CustomAgentToolbar;

